import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal, useToast } from '@repo/design-system/index'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import PortalContainer from '../../../../../components/container/PortalContainer'
import SelectMaterialCard, {
  SelectMaterialCardSkeleton,
} from '../../../../../components/new-portal/lessons/materials/SelectMaterialCard'
import { useEventTracking } from '../../../../../helpers/gtmHelper'
import { useBookMarkedMaterials } from '../../../../../modules/apis/student/material/queries'
import { APP_SCREEN_NAME } from '../../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import { useOnclickHeartPacket } from '../../../../../modules/hooks/common/useOnclickHeartPacket'
import useReturnFullUrl from '../../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_common, n_material_list } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import urls from '../../../../../modules/urls'
import { COLORS } from '../../../../../modules/vars'

export const BookMarkedMaterials = () => {
  const isMobile = useIsMobile(null, 640)
  const { t, isLocaleKO } = useTranslation()
  const { toastInfo } = useToast()
  const router = useAppRouter()
  const { sendClickEvent } = useEventTracking()
  const getFullUrl = useReturnFullUrl()

  const { data: materialsData, isFetching: materialsFetching, refetch } = useBookMarkedMaterials()

  const [selectedHashId, setSelectedHashId] = useState('전체')
  const [materialList, setMaterialList] = useState([])
  const [showBookMarkPopup, setShowBookMarkPopup] = useState<boolean>(false)
  const [popupMaterial, setPopupMaterial] = useState()

  const titleTranslations = (data) => [
    data.title_kr,
    data.title_en,
    '',
    data.title_en,
    data.title_en,
    data.title_en,
    data.title_en,
  ]

  useEffect(() => {
    if (materialsData && !materialsFetching) {
      const filteredCourseList = selectedHashId
        ? materialsData.small_hashtags
            .filter((hash) => hash.title_kr == selectedHashId)
            .flatMap((hash) => hash.courses) || []
        : materialsData.small_hashtags[0]?.courses
      setMaterialList(filteredCourseList)
    }
  }, [materialsData, materialsFetching, selectedHashId])

  const onClickHeartPacket = useOnclickHeartPacket({
    packetItems: materialList,
    setPacketItems: setMaterialList,
    isList: true,
    onAdded: () => {
      console.log('Add')
    },
    onDeleted: () => {
      refetch()
    },
  })

  const BookmarkPopup = ({ onClick }) => {
    return (
      <DSModal
        id="TextOnlySMTwoButtonHorizontalPopup"
        isOpen={showBookMarkPopup}
        setIsOpen={setShowBookMarkPopup}
        size="lg"
      >
        <DSModal.Content>{t(n_material_list.bookmark.delete_title)}</DSModal.Content>
        <DSModal.ButtonContainer direction="horizontal">
          <DSButton
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.OUTLINE}
            customBgColor={COLORS.WHITE}
            onClick={(e) => setShowBookMarkPopup(false)}
          >
            <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
          </DSButton>
          <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.SOLID} className="text-white" onClick={onClick}>
            <DSButton.Label>{t(n_common.btn_delete)}</DSButton.Label>
          </DSButton>
        </DSModal.ButtonContainer>
      </DSModal>
    )
  }

  const handleClickDeleteBookMark = (e, material) => {
    setShowBookMarkPopup(false)
    toastInfo(t(n_material_list.bookmark.toast))
    onClickHeartPacket(e, material)
  }

  return (
    <div className={classNames('mx-[16px] mt-[20px] flex flex-col sm:mt-[40px]')}>
      {materialsData && !materialsFetching ? (
        <div className="Headline2-24Bd">
          {t(titleTranslations(materialsData))} ({materialsData.small_hashtags[0].courses.length})
        </div>
      ) : (
        <div className="h-[34px] w-[240px] rounded-[4px] bg-gray-100"></div>
      )}
      <div className="mt-[16px] flex flex-wrap gap-[8px] border-gray-300 font-medium">
        {materialsData && !materialsFetching ? (
          materialsData?.small_hashtags.map((hash, i) => {
            return (
              <button
                key={i}
                onClick={() => setSelectedHashId(hash.title_kr)}
                className={classNames(
                  selectedHashId === hash.title_kr &&
                    'border-purple-500 bg-purple-50 text-[16px] font-bold text-purple-500',
                  'rounded-[8px] border-[1.5px] px-[16px] py-[8px] text-[16px] '
                )}
              >
                {isLocaleKO ? hash.title_kr : hash.title_en}
              </button>
            )
          })
        ) : (
          <div className="flex flex-row gap-[16px]">
            <button className={'h-[42px] w-[64px] rounded-[8px]  bg-gray-100 px-[16px] py-[8px] text-[16px]'}></button>
            <button className={'h-[42px] w-[64px] rounded-[8px] bg-gray-100 px-[16px] py-[8px] text-[16px]'}></button>
          </div>
        )}
      </div>
      <div>
        {!materialsFetching && materialList?.length > 0 ? (
          <div
            className={classNames(
              'mt-[36px] grid grid-cols-1 gap-y-[10px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
            )}
          >
            {materialList?.map((material) => {
              return (
                <div
                  onClick={() => {
                    sendClickEvent({ button_name: 'material_card', object_id: material.id })
                    router.push(urls.student.newPortal.materials.detail(material.id), {
                      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                      appSendData: { url: getFullUrl(urls.webview.materials.detail(material.id)) },
                    })
                  }}
                  key={material.id}
                >
                  <SelectMaterialCard
                    isList={true}
                    horizontal={isMobile}
                    title={material.title}
                    subtitle={material.subtitle}
                    is_mine={material.is_mine}
                    category={material.category}
                    image_url={material.image_url}
                    is_revised={material.is_revised}
                    is_today={material.is_today}
                    onClickHeart={(e) => {
                      setShowBookMarkPopup(true)
                      setPopupMaterial(material)
                    }}
                    level={material.level}
                    audioLength={material.mp3_running_time_in_minute}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <div className="w-full">
            <div className="mb-[32px] mt-[36px] w-full">
              <section className="">
                <div
                  className={classNames(
                    'flex w-full flex-col gap-[9px] sm:grid sm:grid-cols-2 sm:flex-row md:grid-cols-3 lg:grid-cols-4'
                  )}
                >
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((_, i) => (
                    <div className="w-full" key={i}>
                      <SelectMaterialCardSkeleton horizontal={isMobile} />
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
      <BookmarkPopup onClick={(e) => handleClickDeleteBookMark(e, popupMaterial)} />
    </div>
  )
}
const Index = () => {
  const { t } = useTranslation()
  return (
    <PortalContainer backTo={urls.student.newPortal.myRingle.index}>
      <BookMarkedMaterials />
    </PortalContainer>
  )
}

export default Index
